import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import { normalizeToSupportedLocale } from "./locale";
import {
  defaultLanguage,
  defaultNS,
  namespaces,
  resources,
  supportedLanguages,
  type SupportedLanguage,
} from "./resources";
import { detectBrowserLanguage, getSavedLanguage, saveLanguage } from "./storage";

/**
 * The i18n instance used throughout the application.
 * Uses createInstance() to avoid global singleton issues.
 */
export const i18n = i18next.createInstance();

/**
 * Determines the initial language based on:
 * 1. Saved preference in localStorage (normalized to two-part format)
 * 2. Browser language (normalized to two-part format)
 * 3. Default language (en-US)
 */
const getInitialLanguage = (): SupportedLanguage => {
  // getSavedLanguage() already normalizes legacy values
  const savedLang = getSavedLanguage();
  if (savedLang) {
    return savedLang;
  }

  // detectBrowserLanguage() already normalizes to supported locale
  const browserLang = detectBrowserLanguage();
  if (browserLang) {
    return browserLang;
  }

  return defaultLanguage;
};

/**
 * Initializes the i18n instance with react-i18next.
 * Call this once at application startup before rendering.
 *
 * All t() calls use explicit `namespace:key` syntax, so defaultNS
 * is always 'common' (used only as fallback for unqualified keys).
 *
 * @returns Promise that resolves when i18n is initialized.
 *
 * @example
 * await initI18n();
 */
export const initI18n = async (): Promise<void> => {
  const initialLanguage = getInitialLanguage();

  await i18n.use(initReactI18next).init({
    resources,
    lng: initialLanguage,
    fallbackLng: defaultLanguage,
    supportedLngs: [...supportedLanguages],
    ns: [...namespaces],
    defaultNS,
    fallbackNS: "common", // Always fall back to common for shared keys
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      useSuspense: false, // Disable suspense for simpler integration
    },
  });

  // Persist the initial language if it came from browser detection
  if (!getSavedLanguage()) {
    saveLanguage(initialLanguage);
  }
};

/**
 * Changes the application language and persists the preference.
 * Normalizes the input to a supported two-part locale format.
 *
 * @param language - The language code to switch to (e.g., 'en-US', 'fr-FR', or legacy 'en', 'fr').
 * @returns Promise that resolves when the language is changed.
 */
export const changeBrowserLanguage = async (language: string): Promise<void> => {
  // Normalize the input to a supported locale
  const normalizedLanguage = normalizeToSupportedLocale(language);

  if (!normalizedLanguage) {
    console.warn(`Unsupported language: ${language}. Falling back to ${defaultLanguage}.`);
    await i18n.changeLanguage(defaultLanguage);
    saveLanguage(defaultLanguage);
    return;
  }

  await i18n.changeLanguage(normalizedLanguage);
  saveLanguage(normalizedLanguage);
};

/**
 * Gets the current language.
 *
 * @returns The current language code.
 */
export const getCurrentLanguage = (): string => {
  return i18n.resolvedLanguage || i18n.language || defaultLanguage;
};
